import React, { useState, useEffect } from 'react';
import './AdminCompanyListing.css';
import Dashboard from './Dashboard.js';
import { useNavigate } from 'react-router';
import { useCompany } from '../context/CompanyContext.js';
import {  getIndustryBadgeType,
  getCompanySizeBadgeType,
  getJobsCountBadgeType,
  getDetailedJobsCountBadge,
  getCompanyStatusBadge,
  getIndustryColor,
  formatEmployeeRange,
  getCompanyAge} from '../helper/AdminCompany.js'

const AdminCompanyListing = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [dateRange, setDateRange] = useState('Jul 19 - Jul 25');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); 
//   const [companyId, setCompanyId] = useState(null);

  const [filters, setFilters] = useState({
      industryIds: [],
      companySize: [],
      search: ''
    });

  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [availableCompanySizes] = useState(['1-10', '11-50', '51-200', '201-1000', '501-1000', '1000+']);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();
  
  const fetchCompanies = async (filterParams = {}, page = 1, size = pageSize) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

    if (filterParams.industryIds && filterParams.industryIds.length > 0) {
        filterParams.industryIds.forEach(industry => {
          queryParams.append('industry', industry);
        });
      }

      if (filterParams.company_sizes && filterParams.company_sizes.length > 0) {
        filterParams.company_sizes.forEach(size => {
          queryParams.append('company_size', size);
        });
      }

      if (filterParams.search && filterParams.search.trim()) {
        queryParams.append('query', filters.search.trim());
      }

      // Uncomment this when your API is ready
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/list-companies?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.data || []);
        setTotalCompanies(data.total || 0);
        setTotalCount(data.total || 0);
        setTotalPages(Math.ceil((data.total || 0) / size));

      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch jobs');
      }
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch jobs');
      setLoading(false);
      console.error('Error fetching jobs:', error);
    }
  };

    // Fetch available industries for filter options
  const fetchAvailableIndustries = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/companies/list-industries`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableIndustries(data.industries || data);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
      // Fallback to predefined list
      setAvailableIndustries([
        'technology', 'finance', 'healthcare', 'education', 'retail',
        'manufacturing', 'consulting', 'media', 'real estate', 'automotive'
      ]);
    }
  };

    // Initial data fetch
  useEffect(() => {
    fetchAvailableIndustries();
    fetchCompanies({}, 1, pageSize);
  }, []);

  // useEffect(() => {
  //       fetchCompanies();
  // }, [currentPage, pageSize, filters]);

  const handleFilterChange = (filterType, value, checked) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      
      if (filterType === 'search') {
        newFilters.search = value;
      } else if (filterType === 'industry') {
        // 'value' here is the industry ID
        if (checked) {
          newFilters.industryIds = [...prev.industryIds, value];
        } else {
          newFilters.industryIds = prev.industryIds.filter(id => id !== value);
        }
      } else {
        if (checked) {
          newFilters[filterType] = [...prev[filterType], value];
        } else {
          newFilters[filterType] = prev[filterType].filter(item => item !== value);
        }
      }
      
      return newFilters;
    });
  };

  const applyFilters = () => {
    const filterParams = {};
    
    if (filters.search.trim()) {
      filterParams.search = filters.search.trim();
    }
    
    if (filters.industryIds.length > 0) {
      filterParams.industryIds = filters.industryIds;
    }
    
    if (filters.companySize.length > 0) {
      filterParams.company_sizes = filters.companySize;
    }

    setCurrentPage(1);
    fetchCompanies(filterParams, 1, pageSize);
    setShowFilters(false);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      applyFilters();
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const clearAllFilters = () => {
    setFilters({
      industryIds: [],
      companySize: [],
      search: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
    fetchCompanies({}, 1, pageSize);
  };

  const getIndustryNameById = (industryId) => {
    const industry = availableIndustries.find(ind => ind.id === industryId);
    return industry ? industry.name : `Industry ${industryId}`;
  };


    // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    
    const filterParams = {};
    if (filters.search.trim()) filterParams.search = filters.search.trim();
    if (filters.industryIds.length > 0) filterParams.industryIds = filters.industryIds;
    if (filters.companySize.length > 0) filterParams.company_sizes = filters.companySize;
    
    fetchCompanies(filterParams, page, pageSize);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    
    const filterParams = {};
    if (filters.search.trim()) filterParams.search = filters.search.trim();
    if (filters.industryIds.length > 0) filterParams.industryIds = filters.industryIds;
    if (filters.companySize.length > 0) filterParams.company_sizes = filters.companySize;
    
    fetchCompanies(filterParams, 1, newSize);
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    return filters.industryIds.length + 
           filters.companySize.length + 
           (filters.search.trim() !== '' ? 1 : 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Dashboard page={"company"}>
  <div className="admin-company-listing-container">
    <div className="admin-company-listing-content">
      <div className="admin-company-content-header">
        <div className="admin-company-header-info">
            <h1>Company Listing</h1>
            <p className="admin-company-date-range-text">
              Showing {companies.length} of {totalCount} companies
              {getActiveFilterCount() > 0 && ` (${getActiveFilterCount()} filter${getActiveFilterCount() > 1 ? 's' : ''} applied)`}
            </p>
        </div>
        {/* <div className="admin-company-date-range-selector">
          <button className="admin-company-date-range-btn">
            {dateRange} 📅
          </button>
        </div> */}
      </div>

      <div className="admin-company-list-section">
        <div className="admin-company-section-header">
          <div className="admin-company-header-actions">
            <div className="admin-company-search-container">
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleFilterChange('search', e.target.value);
                }}
                className="admin-company-search-input"
              />
              <span className="admin-company-search-icon">🔍</span>
            </div>
            <button 
              className="admin-company-filters-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <span className="admin-company-filter-icon">⚙️</span>
              Filters
              {getActiveFilterCount() > 0 && (
                <span className="admin-company-filter-count">{getActiveFilterCount()}</span>
              )}
            </button>
          </div>
        </div>

        {showFilters && (
            <div className="admin-company-filters-panel">
              <div className="admin-company-filters-content">
                
                {/* Industry Filter */}
                <div className="admin-company-filter-group">
                  <h4>Industry ({filters.industryIds.length} selected)</h4>
                  <div className="admin-company-filter-options">
                    {availableIndustries.map(industry => (
                      <label key={industry.id} className="admin-company-filter-option">
                        <input
                          type="checkbox"
                          checked={filters.industryIds.includes(industry.id)}
                          onChange={(e) => handleFilterChange('industry', industry.id, e.target.checked)}
                        />
                        <span className="admin-company-checkmark"></span>
                        <span className="admin-company-option-label">
                          {industry.name.charAt(0).toUpperCase() + industry.name.slice(1)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Company Size Filter */}
                <div className="admin-company-filter-group">
                  <h4>Company Size ({filters.companySize.length} selected)</h4>
                  <div className="admin-company-filter-options">
                    {availableCompanySizes.map(size => (
                      <label key={size} className="admin-company-filter-option">
                        <input
                          type="checkbox"
                          checked={filters.companySize.includes(size)}
                          onChange={(e) => handleFilterChange('companySize', size, e.target.checked)}
                        />
                        <span className="admin-company-checkmark"></span>
                        <span className="admin-company-option-label">{size} employees</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Filter Actions */}
                <div className="admin-company-filter-actions">
                  <button onClick={clearAllFilters} className="admin-company-clear-filters-btn">
                    Clear All ({getActiveFilterCount()})
                  </button>
                  <button onClick={applyFilters} className="admin-company-apply-filters-btn">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {getActiveFilterCount() > 0 && (
            <div className="active-filters-display">
              <div className="active-filters-header">
                <span>Active Filters:</span>
                <button onClick={clearAllFilters} className="clear-all-quick-btn">Clear All</button>
              </div>
              <div className="active-filters-list">
               {filters.industryIds.map(industryId => (
                  <span key={industryId} className="filter-chip">
                    Industry: {getIndustryNameById(industryId)}
                    <button 
                      onClick={() => {
                        handleFilterChange('industry', industryId, false);
                        setTimeout(applyFilters, 100);
                      }}
                      className="filter-chip-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {filters.companySize.map(size => (
                  <span key={size} className="filter-chip">
                    Size: {size}
                    <button 
                      onClick={() => {
                        handleFilterChange('companySize', size, false);
                        setTimeout(applyFilters, 100);
                      }}
                      className="filter-chip-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {filters.search && (
                  <span className="filter-chip">
                    Search: "{filters.search}"
                    <button 
                      onClick={() => {
                        setSearchTerm('');
                        handleFilterChange('search', '');
                        setTimeout(applyFilters, 100);
                      }}
                      className="filter-chip-remove"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}

        <div className="admin-company-table">
          <div className="table-header">
            <div className="header-cell role-header">Name</div>
            <div className="header-cell role-header">Industry</div>
            <div className="header-cell date-header">Created</div>
            <div className="header-cell date-header">Employee Range</div>
            <div className="header-cell date-header">Total Jobs</div>
            <div className="header-cell date-header">Active Jobs</div>
          </div>

          <div className="table-body">
            {loading ? 
            (<div className="admin-company-loading-state">Loading companies...</div>) : companies.length > 0 ? (
              companies.map((company) => (
                <div key={company.id} className="table-row" onClick={() => navigate(`/view-company-profile/${company.id}`)}>
                  <div className="table-cell role-cell">
                    <span className="admin-company-name">{company.name}</span>
                  </div>
                  <div className="table-cell role-cell">
                    <span className={`company-industry-badge company-industry-badge--${getIndustryBadgeType(company.industry_name)}`}>
                      {company.industry_name}
                    </span>
                  </div>
                  <div className="table-cell date-cell">
                    <span className="admin-company-date-cell">{formatDate(company.created_at)}</span>
                  </div>
                  <div className="table-cell date-cell">
                    <span className={`company-size-badge company-size-badge--${getCompanySizeBadgeType(company.employee_range)}`}>
                      {company.employee_range}
                    </span>
                  </div>
                  <div className="table-cell applicants-cell">
                    <span className={`jobs-count-badge jobs-count-badge--${getJobsCountBadgeType(company.total_jobs)}`}>
                      {company.total_jobs}
                    </span>
                  </div>
                  <div className="table-cell applicants-cell">
                    <span className={`jobs-count-badge jobs-count-badge--${getJobsCountBadgeType(company.active_jobs)}`}>
                      {company.active_jobs}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-company-empty-state">No companies found</div>
            )}
          </div>
        </div>

        <div className="admin-company-pagination">
          <div className="admin-company-pagination-info">
            <span>View</span>
            <select 
              value={pageSize} 
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="admin-company-page-size-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>Companies per page</span>
          </div>
          
          <div className="admin-company-pagination-controls">
            <button 
              className="admin-company-page-btn"
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`admin-company-page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            
            <button 
              className="admin-company-page-btn"
              onClick={() => handlePageChange(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
          {error && (
        <div className="error-message">
          {error}
        </div>
      )}
  </div>
</Dashboard>
  );
};

export default AdminCompanyListing;